import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { DataSource } from '../../backend';
import { currentBuilderState } from '../builder.state';
import { checkCacheCriteriaParams } from '../function/check-cache-criteria-params.function';
import { checkCacheDateParams } from '../function/check-cache-date-params.function';
import { checkCacheMetricParams } from '../function/check-cache-metric-params.function';
import { checkPeriodTypeParams } from '../function/check-period-type-params.function';
import { checkRawDateParams } from '../function/check-raw-date-params.function';
import {
    loadCacheCriteriaOnCacheDateChange,
    loadCacheCriteriaOnDimensionChange,
    loadCacheCriteriaOnReportTypeChange,
} from '../function/load-cache-criteria.function';
import { loadCacheDates } from '../function/load-cache-dates.function';
import { loadCacheMetrics } from '../function/load-cache-metrics.function';
import { loadMetrics } from '../function/load-metrics.function';
import { loadPeriodTypes } from '../function/load-period-types.function';
import { loadPresets } from '../function/load-presets.function';
import { updateRouterQueryParams } from '../function/update-router-query-params.function';
import { updateSectionLabels } from '../function/update-section-labels.function';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class SelectionService implements OnDestroy {
    private readonly _subj: Subject<void>;

    constructor() {
        this._subj = new Subject();
    }

    ngOnDestroy(): void {
        this._subj.next();
        this._subj.complete();
    }

    onLoad(): void {
        this._load(true);
    }

    onLoadPreset(): void {
        this._load(false);
    }

    onCacheDateChange(): void {
        this._subj.next();

        updateSectionLabels();
        updateRouterQueryParams();

        forkJoin([loadCacheCriteriaOnCacheDateChange(), loadCacheMetrics()])
            .pipe(
                tap(() => {
                    checkCacheCriteriaParams();
                    checkCacheMetricParams();

                    updateSectionLabels();
                    updateRouterQueryParams();
                }),
                takeUntil(this._subj),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    onRawDateChange(wasStart: boolean): void {
        this._subj.next();

        checkRawDateParams(wasStart);

        updateSectionLabels();
        updateRouterQueryParams();
    }

    onReportTypeChange(): void {
        this._subj.next();

        updateSectionLabels();
        updateRouterQueryParams();

        forkJoin([
            loadCacheCriteriaOnReportTypeChange(),
            loadCacheCriteriaOnDimensionChange(true),
            loadCacheMetrics(),
        ])
            .pipe(
                tap(() => {
                    checkCacheCriteriaParams();
                    checkCacheMetricParams();

                    updateSectionLabels();
                    updateRouterQueryParams();
                }),
                takeUntil(this._subj),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    onDimensionsChange(wasSite: boolean = false): void {
        this._subj.next();

        updateSectionLabels();
        updateRouterQueryParams();

        forkJoin([
            loadCacheCriteriaOnDimensionChange(wasSite),
            loadCacheMetrics(),
        ])
            .pipe(
                tap(() => {
                    checkCacheCriteriaParams();
                    checkCacheMetricParams();

                    updateSectionLabels();
                    updateRouterQueryParams();
                }),
                takeUntil(this._subj),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _load(initial: boolean): void {
        this._subj.next();

        const obses = [
            loadCacheCriteriaOnCacheDateChange(),
            loadCacheCriteriaOnReportTypeChange(),
            loadCacheCriteriaOnDimensionChange(true),
        ];

        if (initial) {
            obses.splice(
                0,
                0,
                loadPresets(),
                loadPeriodTypes(),
                loadCacheDates(),
            );
        }

        const state = currentBuilderState();
        switch (state.viewParams.dataSource) {
            case DataSource.Cache:
                obses.push(loadCacheMetrics());
                break;
            case DataSource.Raw:
                obses.push(loadMetrics());
                break;
        }

        forkJoin(obses)
            .pipe(
                tap(() => {
                    checkPeriodTypeParams();
                    // checkDataSourceParams();
                    checkCacheDateParams();
                    checkRawDateParams(true);
                    checkCacheCriteriaParams();
                    checkCacheMetricParams();

                    updateSectionLabels();
                    updateRouterQueryParams();
                }),
                takeUntil(this._subj),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
